import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const getSessionId = () => {
  let id = localStorage.getItem("smartsn_chat_session");
  if (!id) {
    id = `s-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
    localStorage.setItem("smartsn_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const welcomed = useRef(false);

  useEffect(() => {
    if (open && !welcomed.current) {
      welcomed.current = true;
      setMessages([{ role: "assistant", content: t.chat.welcome }]);
    }
  }, [open, t.chat.welcome]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text, lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "…" };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.a
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.8, type: "spring", stiffness: 200, damping: 15 }}
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-button"
        aria-label="WhatsApp"
        className="fixed bottom-6 right-[92px] z-50 w-12 h-12 rounded-full bg-[#25D366] hover:bg-white text-white hover:text-[#25D366] flex items-center justify-center shadow-[0_0_30px_rgba(37,211,102,0.35)] transition-colors duration-300"
      >
        <WhatsAppIcon />
      </motion.a>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 200, damping: 15 }}
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label="Chat"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#FF7900] hover:bg-white text-white hover:text-black flex items-center justify-center shadow-[0_0_40px_rgba(255,121,0,0.4)] transition-colors duration-300"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm h-[480px] flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/70 backdrop-blur-2xl shadow-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-white/5">
              <span className="w-9 h-9 rounded-full bg-[#FF7900]/20 border border-[#FF7900]/40 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#FF7900]" />
              </span>
              <div>
                <p className="text-sm font-semibold">{t.chat.title}</p>
                <p className="text-xs text-white/50">{t.chat.subtitle}</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "self-end bg-[#FF7900] text-white rounded-2xl rounded-br-sm"
                      : "self-start bg-white/10 text-white/90 rounded-2xl rounded-bl-sm"
                  }`}
                >
                  {m.content}
                  {streaming && i === messages.length - 1 && m.role === "assistant" && (
                    <span className="inline-block w-1.5 h-4 bg-[#FF7900] ml-1 animate-pulse align-middle" />
                  )}
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t.chat.placeholder}
                data-testid="chat-input"
                className="flex-1 bg-white/5 border border-white/10 focus:border-[#FF7900] outline-none rounded-full px-4 py-2.5 text-sm placeholder:text-white/30 transition-colors duration-300"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send"
                className="w-10 h-10 rounded-full bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-40 text-white flex items-center justify-center transition-colors duration-300"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
